import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiPackage,
  FiBox,
  FiCheckCircle,
  FiClock,
  FiChevronLeft,
  FiCheck,
  FiAlertTriangle,
  FiHome,
  FiUser,
  FiX,
  FiSearch,
  FiLock,
  FiCoffee,
  FiTag,
  FiStar,
  FiTruck,
  FiInfo,
} from 'react-icons/fi';
import API, { getSchemaName, getImagenUrl } from 'shared/api';
import { fmtMonto } from 'shared/format';
import socket from 'shared/socket';
import { useAuth } from 'shared/context/AuthContext';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const C_light = {
  bg: '#F5F7FA',
  card: '#FFFFFF',
  border: '#E8EAEF',
  line: '#F0F2F5',
  ink: '#0B1A4A',
  text: '#202124',
  muted: '#5f6368',
  faint: '#9aa0a6',
  orange: '#ff6d00',
  blue: '#1a73e8',
  green: '#34a853',
  red: '#ea4335',
  tienda: '#0f3460',
  tiendaLight: '#16213e',
};

const C_dark = {
  bg: '#0f172a',
  card: '#1e293b',
  border: '#334155',
  line: '#334155',
  ink: '#e2e8f0',
  text: '#e2e8f0',
  muted: '#64748b',
  faint: '#475569',
  orange: '#fb923c',
  blue: '#60a5fa',
  green: '#34d399',
  red: '#f87171',
  tienda: '#93c5fd',
  tiendaLight: '#1e3a5f',
};

const getColors = () => (isDark() ? C_dark : C_light);

// Paleta de colores para los banners de mesa (vista restaurante).
const MESA_PALETTE = [
  '#e11d48', '#2563eb', '#059669', '#d97706', '#7c3aed',
  '#0891b2', '#db2777', '#65a30d', '#ea580c', '#4f46e5',
];

const parseProductos = (prods) => {
  if (typeof prods === 'string') {
    try {
      prods = JSON.parse(prods);
    } catch {
      prods = [];
    }
  }
  return Array.isArray(prods) ? prods : [];
};

const cantidadLabel = (p) => {
  const c = Number(p.cantidad) || 0;
  if (p.pesable) {
    if (c >= 1000) return `${(c / 1000).toLocaleString('es-ES', { maximumFractionDigits: 3 })} kg`;
    return `${c} g`;
  }
  return `\u00d7${c}`;
};

const formatearCantidadInsumo = (ins) => {
  const c = Number(ins.cantidad);
  const cant = Number.isFinite(c)
    ? c.toLocaleString('es-ES', { maximumFractionDigits: 3 })
    : '';
  const unidad = ins.unidad_simbolo || ins.unidad_nombre || '';
  return `${cant}${unidad ? ' ' + unidad : ''}`;
};

const SLA_MIN = 30;
const minutosDesde = (f) => {
  if (!f) return 0;
  return Math.floor((Date.now() - new Date(f).getTime()) / 60000);
};

const Thumb = ({ src, size = 56, radius = 12 }) => {
  const [err, setErr] = useState(false);
  useEffect(() => {
    setErr(false);
  }, [src]);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: 'var(--agx-skeleton)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {src && !err ? (
        <img
          src={src}
          alt=""
          onError={() => setErr(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <FiPackage size={Math.round(size * 0.42)} color="var(--agx-faint)" />
      )}
    </div>
  );
};

const ArmadorPanel = ({ sedeFilter, armadorCedula, isReadOnly, esCocinero, cocineroGestionar }) => {
  const COL = getColors();
  const [porArmar, setPorArmar] = useState([]);
  const [listosHoy, setListosHoy] = useState([]);
  const [comandas, setComandas] = useState([]);
  const [tab, setTab] = useState('por_armar');
  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [detalleId, setDetalleId] = useState(null);
  const [checklist, setChecklist] = useState({});
  const [scanValue, setScanValue] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [imagenAmpliada, setImagenAmpliada] = useState(null);
  const [confirmacion, setConfirmacion] = useState(null);
  const [recetaInfo, setRecetaInfo] = useState(null);
  const { tieneVisibilidad, isTipoEmpresa } = useAuth();
  const v = (c) => tieneVisibilidad(c);
  const esRestaurante = isTipoEmpresa('restaurante');
  const esImportador = isTipoEmpresa('importador');
  const esMesero = !esCocinero && esRestaurante;

  const lastFetchRef = useRef({ sede: null, ts: 0 });
  const fetchSeqRef = useRef(0);
  const cargarPanel = useCallback(async () => {
    // Registra el fetch directo para coalescer la recarga debounced que llega
    // por el echo del socket tras una mutacion propia (evita 2 fetches por tachado).
    const seq = ++fetchSeqRef.current;
    lastFetchRef.current = { sede: sedeFilter || '', ts: Date.now() };
    try {
      const params = new URLSearchParams();
      params.append('schema_name', getSchemaName());
      if (sedeFilter) params.append('codigo_sede', sedeFilter);
      const res = await API.get(`/pedidos/armador/panel?${params.toString()}`);
      if (res.data.status === 'success') {
        // Descarta respuestas obsoletas (otro fetch o mutacion posterior) para no
        // sobrescribir con datos stale que reviertan un tachado ya optimista.
        if (seq !== fetchSeqRef.current) return;
        setPorArmar(res.data.data.por_armar || []);
        setListosHoy(res.data.data.listos_hoy || []);
        setComandas(res.data.data.comandas || []);
      }
    } catch (e) {
      console.error('Error cargando panel armador:', e);
    }
  }, [sedeFilter]);

  // Recarga con debounce: los eventos socket (connect / pedido_actualizado) pueden
  // llegar en rafaga (reconexion + emisiones por cada tachado). Coalescer en una sola
  // llamada evita saturar /pedidos/armador/panel cuando hay varias tabs abiertas.
  const reloadTimerRef = useRef(null);
  const recargarDebounced = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      const last = lastFetchRef.current;
      // Si ya hubo un fetch reciente para esta sede (el fetch directo posterior a
      // una mutacion propia), el echo del socket es redundante: se omite.
      if (last.sede === (sedeFilter || '') && Date.now() - last.ts < 600) {
        return;
      }
      cargarPanel();
    }, 400);
  }, [cargarPanel, sedeFilter]);
  useEffect(() => {
    return () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    };
  }, []);

  useEffect(() => {
    cargarPanel();
  }, [cargarPanel]);

  const [socketConectado, setSocketConectado] = useState(socket.connected);
  useEffect(() => {
    const onConnect = () => {
      setSocketConectado(true);
      recargarDebounced();
    };
    const onDisconnect = () => setSocketConectado(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [recargarDebounced]);

  const detalleIdRef = useRef(null);
  detalleIdRef.current = detalleId;

  useEffect(() => {
    const handler = (data) => {
      recargarDebounced();
      const idActual = detalleIdRef.current;
      if (
        data &&
        data.pedido_id &&
        parseInt(data.pedido_id) === parseInt(idActual) &&
        data.items_armados !== undefined
      ) {
        const arr = Array.isArray(data.items_armados) ? data.items_armados : [];
        const obj = {};
        arr.forEach((i) => {
          obj[i] = true;
        });
        // Merge (no replace): si el echo sobrescribiera el checklist, una clave
        // recien destachada caeria al fallback _checked (stale) y produciria el
        // parpadeo marca -> desmarca -> marca. Con merge, las claves destachadas
        // conservan su valor false y el tachado queda fluido y estable.
        setChecklist((prev) => ({ ...prev, ...obj }));
      }
    };
    socket.on('pedido_actualizado', handler);
    return () => {
      socket.off('pedido_actualizado', handler);
    };
  }, [recargarDebounced]);

  useEffect(() => {
    if (detalleId == null) {
      setChecklist({});
      return;
    }
    const p = [...porArmar, ...listosHoy, ...comandas].find((x) => x.id === detalleId);
    let arr = p?.items_armados;
    if (typeof arr === 'string') {
      try {
        arr = JSON.parse(arr);
      } catch {
        arr = [];
      }
    }
    const obj = {};
    (Array.isArray(arr) ? arr : []).forEach((i) => {
      obj[i] = true;
    });
    setChecklist(obj);
  }, [detalleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const detalle =
    detalleId != null
      ? [...porArmar, ...listosHoy, ...comandas].find((p) => p.id === detalleId) || null
      : null;

  const esLibre = (p) => !p.id_armador && !p.id_cocinero;
  const esMio = (p) => p.id_armador === armadorCedula || p.id_cocinero === armadorCedula;
  const bloqueado = (p) =>
    !!(
      (p.id_armador && p.id_armador !== armadorCedula) ||
      (p.id_cocinero && p.id_cocinero !== armadorCedula)
    );
  const esListo = (p) => p.estado === 'listo';

  const marcarListo = async (id) => {
    try {
      if (esCocinero && cocineroGestionar) {
        await API.put(`/pedidos/${id}/marcar-cocina-listo`);
      } else {
        await API.put(`/pedidos/${id}/marcar-listo`, {
          schema_name: getSchemaName(),
          armador_cedula: armadorCedula,
        });
      }
      setDetalleId(null);
      setTab('listos');
      cargarPanel();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    }
  };
  // ─── Confirmación de tachado (modal inmediato) ───
  const pedirConfirmacion = (mensaje, onConfirm) => setConfirmacion({ mensaje, onConfirm });
  const cancelarConfirmacion = () => setConfirmacion(null);
  const confirmarAccion = () => {
    const c = confirmacion;
    setConfirmacion(null);
    if (c && typeof c.onConfirm === 'function') c.onConfirm();
  };
  const confirmModal = confirmacion ? (
    <div
      onClick={cancelarConfirmacion}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: 'armConfirmIn 0.12s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COL.card,
          borderRadius: 18,
          width: '100%',
          maxWidth: 340,
          padding: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          border: `1px solid ${COL.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: COL.orange + '1f',
              color: COL.orange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FiCheck size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: COL.ink, lineHeight: 1.3, wordBreak: 'break-word' }}>
              {confirmacion.mensaje}
            </div>
            <div style={{ fontSize: 12, color: COL.muted, marginTop: 4 }}>
              ¿Confirmas esta acción?
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button
            onClick={cancelarConfirmacion}
            style={{
              flex: 1,
              padding: 11,
              borderRadius: 11,
              border: `1px solid ${COL.border}`,
              background: COL.bg,
              color: COL.muted,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={confirmarAccion}
            style={{
              flex: 1,
              padding: 11,
              borderRadius: 11,
              border: 'none',
              background: COL.green,
              color: '#fff',
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ─── Modal de receta (producto compuesto) ───
  const abrirReceta = (pr) => {
    if (pr && pr.receta) {
      setRecetaInfo({ ...pr.receta, producto: pr.descripcion || pr.nombre || '' });
    }
  };
  const cerrarReceta = () => setRecetaInfo(null);
  const infoBtn = (pr) =>
    pr && pr.es_compuesto ? (
      <button
        onClick={(e) => {
          e.stopPropagation();
          abrirReceta(pr);
        }}
        title="Ver receta"
        style={{
          border: 'none',
          background: COL.blue + '1a',
          color: COL.blue,
          width: 28,
          height: 28,
          borderRadius: 9,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <FiInfo size={15} />
      </button>
    ) : null;
  const recetaModal = recetaInfo ? (
    <div
      onClick={cerrarReceta}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 3100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        animation: 'armConfirmIn 0.15s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COL.card,
          borderRadius: 18,
          width: '100%',
          maxWidth: 420,
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          border: `1px solid ${COL.border}`,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px 18px',
            borderBottom: `1px solid ${COL.border}`,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: COL.blue + '1a',
              color: COL.blue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FiInfo size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: COL.ink,
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {recetaInfo.nombre || 'Receta'}
            </div>
            {recetaInfo.producto && (
              <div style={{ fontSize: 12, color: COL.muted, marginTop: 2 }}>
                {recetaInfo.producto}
              </div>
            )}
          </div>
          <button
            onClick={cerrarReceta}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: COL.muted,
              padding: 4,
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <FiX size={20} />
          </button>
        </div>
        <div style={{ padding: '14px 18px', overflowY: 'auto' }}>
          {!recetaInfo.insumos || recetaInfo.insumos.length === 0 ? (
            <div style={{ fontSize: 13, color: COL.muted, textAlign: 'center', padding: '20px 0' }}>
              Esta receta no tiene insumos registrados.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recetaInfo.insumos.map((ins, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    background: COL.bg,
                    borderRadius: 12,
                    border: `1px solid ${COL.border}`,
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: COL.orange,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      color: COL.ink,
                      minWidth: 0,
                      wordBreak: 'break-word',
                    }}
                  >
                    {ins.nombre}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: COL.orange,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatearCantidadInsumo(ins)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const toggleLockRef = useRef(false);
  const persistChecklist = async (obj, snapshot) => {
    if (!detalle) return;
    const id = detalle.id;
    try {
      if (esCocinero && cocineroGestionar) {
        const items = Object.keys(obj).filter((k) => obj[k]);
        await API.put(`/pedidos/${id}/cocina-checklist`, items);
      } else {
        const items = Object.keys(obj).filter((k) => obj[k]);
        await API.put(`/pedidos/${id}/armado-checklist`, { schema_name: getSchemaName(), items });
      }
    } catch (e) {
      if (snapshot) setChecklist(snapshot);
      console.error('Error guardando checklist:', e);
    }
  };
  const toggleItem = (i, skipConfirm = false) => {
    if (toggleLockRef.current) return;
    const ejecutar = () => {
      toggleLockRef.current = true;
      fetchSeqRef.current++;
      setTimeout(() => {
        toggleLockRef.current = false;
      }, 350);
      setChecklist((prev) => {
        const snapshot = { ...prev };
        const next = { ...prev, [i]: !prev[i] };
        persistChecklist(next, snapshot);
        return next;
      });
    };
    if (skipConfirm) {
      ejecutar();
      return;
    }
    const pr = productosVisibles(detalle).find((x) => String(idxOf(x)) === String(i));
    const nombre = pr ? pr.descripcion || pr.nombre || 'producto' : 'este artículo';
    const accion = checklist[i] ? 'desmarcar' : 'tachar';
    pedirConfirmacion(`¿Deseas ${accion} "${nombre}"?`, ejecutar);
  };
  const onScan = (code) => {
    const c = String(code || '').trim();
    setScanValue('');
    if (!c || !detalle) return;
    const visibles = productosVisibles(detalle);
    const idx = visibles.findIndex(
      (p) => String(p.codigo || '').trim() === c || String(p.codigo_barra || '').trim() === c
    );
    if (idx >= 0) {
      const key = idxOf(visibles[idx]);
      if (!checklist[key]) toggleItem(key, true);
      setScanMsg('ok');
      setTimeout(() => setScanMsg(''), 800);
    } else {
      setScanMsg('err');
      setTimeout(() => setScanMsg(''), 1500);
    }
  };
  const formatearHora = (f) =>
    f ? new Date(f).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';

  const tieneItemsCocina = (p) => {
    const prods = parseProductos(p.productos);
    return prods.some((pr) => pr.es_cocina === true);
  };

  const productosVisibles = (p) => {
    if (esCocinero) {
      const items = Array.isArray(p.cocina_items) ? p.cocina_items : [];
      if (items.length > 0) return items;
      // Fallback: si el backend no expuso cocina_items (datos previos), filtra productos.
      return parseProductos(p.productos).filter((pr) => pr.es_cocina === true);
    }
    // El armador usa las unidades expandidas (3 hamburguesas = 3 items separados).
    const units = Array.isArray(p.unidades) ? p.unidades : null;
    if (units && units.length > 0) return units;
    return parseProductos(p.productos);
  };

  // Clave del checklist: las unidades expandidas traen `_index` (string, p.ej. "0.0").
  // Los productos legacy traen `_cocina_index` (entero o "i:ci").
  const idxOf = (pr) => (pr._index != null ? pr._index : pr._cocina_index);

  // Estado de check de una unidad. Prioriza el checklist local; si la clave no esta
  // (comanda legacy con items_armados previos tipo "0"), cae al flag _checked del backend
  // (legacy-aware), de modo que las comandas en vuelo no aparezcan sin tachar.
  const isChecked = (pr) => {
    const idx = idxOf(pr);
    if (idx == null) return false;
    const v = checklist[idx];
    if (v !== undefined) return v;
    return esMesero ? !!pr.mesero_checked : !!pr._checked;
  };

  // En restaurante (mesero), un item de cocina queda bloqueado SOLO hasta que la cocina
  // lo tacha (_checked). Despues queda disponible para tachar por el mesero.
  // En distribuidor (armador, sin cocina) los items de cocina siempre estan bloqueados.
  const esItemBloqueado = (pr) => {
    if (esCocinero) return false;
    if (esMesero) return pr.es_cocina === true && !pr._checked;
    return pr.es_cocina === true;
  };

  const cocinaCompletados = (p) => {
    if (esCocinero) return null;
    return { total: p.cocina_total || 0, completados: p.cocina_completados || 0 };
  };

  // ─── Toggle inline para la vista plana de restaurante ───
  const toggleItemRestaurante = (pedido, pr) => {
    if (esItemBloqueado(pr)) return;
    const nombre = pr.descripcion || pr.nombre || 'este artículo';
    const esCocina = esCocinero;
    const checkField = esCocina ? '_checked' : 'mesero_checked';
    const isOn = !!pr[checkField];
    const accion = isOn ? 'desmarcar' : 'tachar';
    pedirConfirmacion(`¿Deseas ${accion} "${nombre}"?`, () => ejecutarToggleRestaurante(pedido, pr));
  };

  const ejecutarToggleRestaurante = async (pedido, pr) => {
    // Invalida cualquier fetch en vuelo: una respuesta vieja podria sobrescribir el
    // tachado optimista con datos stale (causa el parpadeo marca->desmarca->marca).
    fetchSeqRef.current++;
    const idx = String(idxOf(pr));
    const esCocina = esCocinero;
    const checkField = esCocina ? '_checked' : 'mesero_checked';
    const isOn = !!pr[checkField];

    const updatePedido = (p) => {
      const clone = { ...p };
      if (esCocina) {
        clone.cocina_items = (clone.cocina_items || []).map((u) =>
          String(u._index) === idx ? { ...u, _checked: !isOn } : u
        );
      } else {
        clone.unidades = (clone.unidades || []).map((u) =>
          String(u._index) === idx ? { ...u, mesero_checked: !isOn } : u
        );
      }
      return clone;
    };

    const setEnListas = (fn) => {
      setPorArmar((a) => a.map((x) => (x.id === pedido.id ? fn(x) : x)));
      setComandas((a) => a.map((x) => (x.id === pedido.id ? fn(x) : x)));
      setListosHoy((a) => a.map((x) => (x.id === pedido.id ? fn(x) : x)));
    };
    setEnListas(updatePedido);

    const nextKeys = (p) => {
      if (esCocina) {
        return (p.cocina_items || []).filter((u) => u._checked).map((u) => String(u._index));
      }
      return (p.unidades || []).filter((u) => u.mesero_checked).map((u) => String(u._index));
    };

    try {
      if (esCocina && cocineroGestionar) {
        await API.put(`/pedidos/${pedido.id}/cocina-checklist`, nextKeys(updatePedido(pedido)));
      } else if (esCocina) {
        return;
      } else {
        await API.put(`/pedidos/${pedido.id}/mesero-checklist`, nextKeys(updatePedido(pedido)));
      }
    } catch (e) {
      console.error('Error guardando checklist:', e);
    }
    cargarPanel();
  };

  const styleTag = (
    <style>{`
      .arm-wrap { width: 100%; max-width: 640px; margin: 0 auto; height: 100%; display: flex; flex-direction: column; background: ${COL.bg}; }
      .arm-scroll { flex: 1; overflow-y: auto; -webkit-overflow-scrolling: touch; }
      .arm-bar { flex-shrink: 0; }
      .arm-row { transition: background .12s ease; }
      .arm-row:active { background: ${isDark() ? '#334155' : '#f6f8fb'}; }
      @keyframes armConfirmIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
      @media (max-width: 768px) {
        .arm-thumb-detail { width: 52px !important; height: 52px !important; }
      }
    `}</style>
  );

  // ─── Importador: una sola linea por producto con la cantidad total ───
  const lineasImportador = (p) => {
    const units = Array.isArray(p.unidades) ? p.unidades : [];
    if (units.length === 0) {
      // Fallback: productos originales (ya agregados por cantidad).
      return parseProductos(p.productos).map((pr, i) => ({
        ...pr,
        _idxList: [String(pr._cocina_index ?? i)],
        _cantTotal: Number(pr.cantidad) || 1,
        _pesable: !!pr.pesable,
      }));
    }
    const map = new Map();
    units.forEach((u) => {
      const idx = String(u._index ?? u._cocina_index ?? '');
      // Formato del indice:
      //   producto normal: "i.k"                 -> agrupar por producto "i"
      //   componente combo: "i.k:ci"             -> agrupar por componente "i:ci"
      const [izq, ci] = idx.split(':');
      const base = ci !== undefined
        ? `${izq.split('.')[0]}:${ci}`
        : izq.includes('.') ? izq.split('.')[0] : izq;
      if (!map.has(base)) {
        map.set(base, { ...u, _idxList: [], _cantTotal: 0, _pesable: !!u.pesable });
      }
      const g = map.get(base);
      g._idxList.push(idx);
      if (g._pesable) {
        g._cantTotal = Number(u.cantidad) || g._cantTotal || 0;
      } else {
        g._cantTotal = (g._cantTotal || 0) + (Number(u.cantidad) || 1);
      }
    });
    return Array.from(map.values());
  };

  const toggleLineaImportador = (linea) => {
    const idxList = linea._idxList || [];
    if (idxList.length === 0) return;
    const todos = idxList.every((k) => !!checklist[k]);
    const nombre = linea.descripcion || linea.nombre || 'este producto';
    const accion = todos ? 'desmarcar' : 'tachar';
    pedirConfirmacion(`¿Deseas ${accion} "${nombre}"?`, () => {
      setChecklist((prev) => {
        const snapshot = { ...prev };
        const next = { ...prev };
        idxList.forEach((k) => {
          next[k] = !todos;
        });
        persistChecklist(next, snapshot);
        return next;
      });
    });
  };

  const cantidadLineaImportador = (linea) => {
    const c = Number(linea._cantTotal) || 0;
    if (linea._pesable) {
      if (c >= 1000) return `${(c / 1000).toLocaleString('es-ES', { maximumFractionDigits: 3 })} kg`;
      return `${c} g`;
    }
    return `\u00d7${c}`;
  };

  // ─── Tachar todos los artículos de un pedido (importador) ───
  const marcarTodosImportador = () => {
    if (!detalle) return;
    const lineas = lineasImportador(detalle);
    const snapshot = { ...checklist };
    const next = { ...checklist };
    let cambia = false;
    lineas.forEach((ln) =>
      (ln._idxList || []).forEach((k) => {
        if (!next[k]) {
          next[k] = true;
          cambia = true;
        }
      })
    );
    if (!cambia) return;
    setChecklist(next);
    persistChecklist(next, snapshot);
  };

  // ─── Tachar todos los artículos (armador/cocinero, detalle) ───
  const marcarTodosDetalle = () => {
    if (!detalle) return;
    const visibles = productosVisibles(detalle);
    const snapshot = { ...checklist };
    const next = { ...checklist };
    let cambia = false;
    visibles.forEach((pr) => {
      if (esItemBloqueado(pr)) return;
      const idx = idxOf(pr);
      if (idx == null) return;
      if (!next[idx]) {
        next[idx] = true;
        cambia = true;
      }
    });
    if (!cambia) return;
    setChecklist(next);
    persistChecklist(next, snapshot);
  };

  // ─── Tachar todos los artículos (restaurante, vista plana) ───
  const marcarTodosRestaurante = (pedido) => {
    const prods = productosVisibles(pedido);
    if (!prods || prods.length === 0) return;
    fetchSeqRef.current++;
    const esCocina = esCocinero;
    const updatePedido = (p) => {
      const clone = { ...p };
      if (esCocina) {
        clone.cocina_items = (clone.cocina_items || []).map((u) =>
          esItemBloqueado(u) ? u : { ...u, _checked: true }
        );
      } else {
        clone.unidades = (clone.unidades || []).map((u) =>
          esItemBloqueado(u) ? u : { ...u, mesero_checked: true }
        );
      }
      return clone;
    };
    const setEnListas = (fn) => {
      setPorArmar((a) => a.map((x) => (x.id === pedido.id ? fn(x) : x)));
      setComandas((a) => a.map((x) => (x.id === pedido.id ? fn(x) : x)));
      setListosHoy((a) => a.map((x) => (x.id === pedido.id ? fn(x) : x)));
    };
    setEnListas(updatePedido);
    const nextKeys = (p) => {
      if (esCocina) {
        return (p.cocina_items || []).filter((u) => u._checked).map((u) => String(u._index));
      }
      return (p.unidades || []).filter((u) => u.mesero_checked).map((u) => String(u._index));
    };
    (async () => {
      try {
        if (esCocina && cocineroGestionar) {
          await API.put(`/pedidos/${pedido.id}/cocina-checklist`, nextKeys(updatePedido(pedido)));
        } else if (!esCocina) {
          await API.put(`/pedidos/${pedido.id}/mesero-checklist`, nextKeys(updatePedido(pedido)));
        }
      } catch (e) {
        console.error('Error guardando checklist:', e);
      }
      cargarPanel();
    })();
  };

  if (detalle && !esRestaurante && esImportador && !esCocinero) {
    const lineas = lineasImportador(detalle).sort((a, b) =>
      (a.descripcion || a.nombre || '').localeCompare(b.descripcion || b.nombre || '', 'es', { sensitivity: 'base' })
    );
    const listo = esListo(detalle);
    const interactivo = !isReadOnly && !listo && !bloqueado(detalle);
    const todoMarcado =
      lineas.length === 0 || lineas.every((ln) => (ln._idxList || []).every((k) => !!checklist[k]));

    return (
      <div style={{ height: '100%', width: '100%', background: COL.bg, overflow: 'hidden' }}>
        {styleTag}
        {confirmModal}
        {recetaModal}
        <div className="arm-wrap">
          <div
            className="arm-bar"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              background: COL.card,
              borderBottom: `1px solid ${COL.border}`,
            }}
          >
            <button
              onClick={() => setDetalleId(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: COL.ink,
                display: 'flex',
                padding: '2px',
                flexShrink: 0,
              }}
            >
              <FiChevronLeft size={24} />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '16px', fontWeight: 800, color: COL.ink }}>
                Pedido #{detalle.ticket_numero || detalle.id}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: COL.muted,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {detalle.cliente}
              </div>
            </div>
            {detalle.sede_nombre && (
              <span style={{ ...S.chip, background: COL.blue + '14', color: COL.blue }}>
                {detalle.sede_nombre}
              </span>
            )}
            {listo ? (
              <span style={{ ...S.chip, background: COL.green + '1a', color: COL.green }}>
                <FiCheckCircle size={13} /> Listo
              </span>
            ) : null}
          </div>

          <div className="arm-scroll">
            <div style={{ padding: '14px 16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 14px',
                  background: COL.card,
                  border: `1px solid ${COL.border}`,
                  borderRadius: '14px',
                  marginBottom: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: COL.text }}>
                    Artículos ({lineas.length})
                  </span>
                  {interactivo && !listo && (
                    <button
                      onClick={() =>
                        pedirConfirmacion('¿Tachar todos los artículos de este pedido?', marcarTodosImportador)
                      }
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        border: `1px solid ${COL.green}`,
                        background: COL.green + '14',
                        color: COL.green,
                        borderRadius: '999px',
                        padding: '4px 11px',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      <FiCheck size={12} /> Tachar todos
                    </button>
                  )}
                </div>
                {v('vis.pedidos.total') && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: COL.faint, fontWeight: 600 }}>TOTAL</div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: COL.ink }}>
                      ${typeof detalle.total === 'number' ? fmtMonto(detalle.total) : detalle.total}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {lineas.map((ln, i) => {
                  const checked = (ln._idxList || []).every((k) => !!checklist[k]);
                  const puede = interactivo;
                  const cant = cantidadLineaImportador(ln);
                  return (
                    <div
                      key={i}
                      onClick={() => (puede ? toggleLineaImportador(ln) : null)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 14px',
                        borderRadius: '16px',
                        background: checked
                          ? isDark() ? '#064e3b' : '#ecfdf5'
                          : COL.card,
                        border: `1px solid ${checked ? COL.green + '55' : COL.border}`,
                        borderLeft: `5px solid ${checked ? COL.green : COL.orange}`,
                        cursor: puede ? 'pointer' : 'default',
                        opacity: checked ? 0.75 : 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setImagenAmpliada(getImagenUrl(ln.imagen_url));
                        }}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: 'pointer',
                          flexShrink: 0,
                        }}
                      >
                        <Thumb src={getImagenUrl(ln.imagen_url)} size={60} radius={12} />
                      </button>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            color: checked ? COL.muted : COL.ink,
                            textDecoration: checked ? 'line-through' : 'none',
                            lineHeight: 1.3,
                            wordBreak: 'break-word',
                          }}
                        >
                          {ln.descripcion || ln.nombre || `Producto ${i + 1}`}
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                            marginTop: '6px',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              fontWeight: 800,
                              color: '#fff',
                              background: COL.orange,
                              padding: '4px 12px',
                              borderRadius: '999px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {cant}
                          </span>
                          {ln.codigo && (
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: COL.muted,
                                background: COL.line,
                                padding: '3px 9px',
                                borderRadius: '8px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Cod: {ln.codigo}
                            </span>
                          )}
                          {ln.referencia && (
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: COL.blue,
                                background: COL.blue + '14',
                                padding: '3px 9px',
                                borderRadius: '8px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Ref: {ln.referencia}
                            </span>
                          )}
                          {ln.marca && (
                            <span style={{ fontSize: '11px', color: COL.faint, whiteSpace: 'nowrap' }}>
                              {ln.marca}
                            </span>
                          )}
                        </div>
                      </div>

                      {infoBtn(ln)}
                      <div
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '9px',
                          border: `2px solid ${checked ? COL.green : COL.faint}`,
                          background: checked ? COL.green : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {checked && <FiCheck size={15} color="#fff" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              {interactivo && !listo && (
                <button
                  onClick={() => marcarListo(detalle.id)}
                  disabled={!todoMarcado}
                  style={{
                    marginTop: '14px',
                    width: '100%',
                    padding: '14px',
                    borderRadius: '14px',
                    border: 'none',
                    fontSize: '15px',
                    fontWeight: 800,
                    cursor: todoMarcado ? 'pointer' : 'not-allowed',
                    color: '#fff',
                    background: todoMarcado ? COL.green : COL.faint,
                    letterSpacing: '0.5px',
                  }}
                >
                  {todoMarcado ? 'Marcar como listo' : `Faltan ${lineas.filter((ln) => !(ln._idxList || []).every((k) => !!checklist[k])).length} productos`}
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              padding: '8px 16px',
              borderTop: `1px solid ${COL.border}`,
              background: COL.card,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '12px',
                background: COL.line,
              }}
            >
              <FiSearch size={14} color={COL.faint} />
              <input
                value={scanValue}
                onChange={(e) => {
                  setScanValue(e.target.value);
                  onScan(e.target.value);
                }}
                placeholder="Escanear codigo de barra..."
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontSize: '13px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  color: COL.text,
                }}
              />
              {scanMsg === 'ok' && <FiCheck size={14} color={COL.green} />}
              {scanMsg === 'err' && <FiX size={14} color={COL.red} />}
            </div>
          </div>
        </div>

        {imagenAmpliada && (
          <div
            onClick={() => setImagenAmpliada(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.8)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
          >
            <img
              src={imagenAmpliada}
              alt="Producto"
              style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12 }}
            />
          </div>
        )}
      </div>
    );
  }

  if (detalle && !esRestaurante) {
    const isTiendaDetalle = detalle.tipo_pedido === 'tienda' || !!detalle.mesa_label;
    const visibles = productosVisibles(detalle);
    const allProdsThumb = parseProductos(detalle.productos);
    const listo = esListo(detalle);
    const cocinaStats = cocinaCompletados(detalle);
    const hechos = visibles.filter((pr) => {
      return isChecked(pr);
    }).length;
    const armables = visibles.filter((pr) => !esItemBloqueado(pr)).length;
    const todoMarcado =
      armables === 0 ||
      visibles
        .filter((pr) => !esItemBloqueado(pr))
        .every((pr) => isChecked(pr));
    const pct = visibles.length ? Math.round((hechos / visibles.length) * 100) : 0;
    const interactivo =
      !isReadOnly && !listo && (esCocinero ? cocineroGestionar : !bloqueado(detalle));

    return (
      <div style={{ height: '100%', width: '100%', background: COL.bg, overflow: 'hidden' }}>
        {styleTag}
        {confirmModal}
        {recetaModal}
        <div className="arm-wrap">
          <div
            className="arm-bar"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              background: COL.card,
              borderBottom: `1px solid ${COL.border}`,
            }}
          >
            <button
              onClick={() => setDetalleId(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: COL.ink,
                display: 'flex',
                padding: '2px',
                flexShrink: 0,
              }}
            >
              <FiChevronLeft size={24} />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              {isTiendaDetalle && detalle.mesa_label && (
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 800,
                    color: COL.tienda,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <FiHome size={15} /> Mesa {detalle.mesa_label}
                </div>
              )}
              <div
                style={{
                  fontSize: isTiendaDetalle && detalle.mesa_label ? '13px' : '16px',
                  fontWeight: isTiendaDetalle && detalle.mesa_label ? 600 : 800,
                  color: COL.ink,
                  marginTop: isTiendaDetalle && detalle.mesa_label ? 1 : 0,
                }}
              >
                Pedido #{detalle.ticket_numero || detalle.id}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: COL.muted,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {detalle.cliente}
              </div>
            </div>
            {!isTiendaDetalle && detalle.sede_nombre && (
              <span style={{ ...S.chip, background: COL.blue + '14', color: COL.blue }}>
                {detalle.sede_nombre}
              </span>
            )}
            {listo ? (
              <span style={{ ...S.chip, background: COL.green + '1a', color: COL.green }}>
                <FiCheckCircle size={13} /> Listo
              </span>
            ) : null}
          </div>

          <div className="arm-scroll">
            <div style={{ padding: '14px 16px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  background: COL.card,
                  border: `1px solid ${COL.border}`,
                  borderRadius: '14px',
                  padding: '12px 14px',
                }}
              >
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {isTiendaDetalle ? (
                    <span
                      style={{
                        fontSize: '12px',
                        color: COL.tienda,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <FiHome size={12} />{' '}
                      {detalle.mesa_label
                        ? `Mesa ${detalle.mesa_label}`
                        : detalle.mesa_id
                          ? `Mesa #${detalle.mesa_id}`
                          : 'Tienda'}
                    </span>
                  ) : (
                    detalle.sede_nombre && (
                      <span
                        style={{
                          fontSize: '12px',
                          color: COL.muted,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <FiHome size={12} /> {detalle.sede_nombre}
                      </span>
                    )
                  )}
                  <span
                    style={{
                      fontSize: '12px',
                      color: COL.muted,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <FiClock size={12} /> {formatearHora(detalle.fechapedido)}
                  </span>
                  {detalle.armador_nombre && (
                    <span
                      style={{
                        fontSize: '12px',
                        color: COL.orange,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <FiUser size={12} /> {detalle.armador_nombre}
                    </span>
                  )}
                  {detalle.id_cocinero && (
                    <span
                      style={{
                        fontSize: '12px',
                        color: COL.orange,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <FiUser size={12} /> Cocinero asignado
                    </span>
                  )}
                </div>
                {v('vis.pedidos.total') && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: COL.faint, fontWeight: 600 }}>TOTAL</div>
                    <div style={{ fontSize: '20px', fontWeight: 800, color: COL.ink }}>
                      $
                      {typeof detalle.total === 'number' ? fmtMonto(detalle.total) : detalle.total}
                    </div>
                  </div>
                )}
              </div>

              {detalle.notas_asesor && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '10px 12px',
                    borderRadius: '12px',
                    background: isDark() ? '#78350f' : '#fff4ec',
                    color: isDark() ? '#fcd34d' : '#b35100',
                    fontSize: '12px',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-start',
                  }}
                >
                  <FiAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />{' '}
                  <span>{detalle.notas_asesor}</span>
                </div>
              )}

              {esCocinero && visibles.length < allProdsThumb.length && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    background: COL.tienda + '18',
                    color: COL.tienda,
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  <FiCoffee size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Solo items de cocina ({visibles.length} de {allProdsThumb.length})
                </div>
              )}
              {!esCocinero && cocinaStats && cocinaStats.total > 0 && !listo && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    background:
                      cocinaStats.completados === cocinaStats.total
                        ? isDark()
                          ? '#064e3b'
                          : '#ecfdf5'
                        : isDark()
                          ? '#78350f'
                          : '#fff4ec',
                    color:
                      cocinaStats.completados === cocinaStats.total
                        ? isDark()
                          ? '#6ee7b7'
                          : '#0b5e2e'
                        : isDark()
                          ? '#fcd34d'
                          : '#b35100',
                    fontSize: '12px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FiCoffee size={13} />
                  {cocinaStats.completados === cocinaStats.total
                    ? 'Cocina completada'
                    : `Pendiente cocina (${cocinaStats.completados}/${cocinaStats.total})`}
                </div>
              )}

              <div style={{ marginTop: '12px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: COL.text }}>
                      Items ({hechos}/{visibles.length})
                    </span>
                    {interactivo && !listo && (
                      <button
                        onClick={() =>
                          pedirConfirmacion('¿Tachar todos los artículos de este pedido?', marcarTodosDetalle)
                        }
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          border: `1px solid ${COL.green}`,
                          background: COL.green + '14',
                          color: COL.green,
                          borderRadius: '999px',
                          padding: '4px 11px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        <FiCheck size={12} /> Tachar todos
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div
                      style={{
                        width: '100px',
                        height: '6px',
                        borderRadius: '999px',
                        background: COL.line,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          borderRadius: '999px',
                          background: pct === 100 ? COL.green : COL.orange,
                          transition: 'width .25s',
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: pct === 100 ? COL.green : COL.muted,
                      }}
                    >
                      {pct}%
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {visibles.map((pr, i) => {
                    const idx = idxOf(pr);
                    const checked = isChecked(pr);
                    const bloqueado = esItemBloqueado(pr);
                    const puedeInteractuar = interactivo && !bloqueado;
                    return (
                      <div
                        key={i}
                        onClick={() => (puedeInteractuar ? toggleItem(idx) : null)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          borderRadius: '12px',
                          background: checked
                            ? isDark()
                              ? '#064e3b'
                              : '#ecfdf5'
                            : bloqueado
                              ? isDark()
                                ? '#1e293b'
                                : '#fafafa'
                              : COL.card,
                          border: `1px solid ${checked ? COL.green + '55' : bloqueado ? (isDark() ? '#334155' : '#e8e8e8') : COL.border}`,
                          cursor: puedeInteractuar ? 'pointer' : 'default',
                          opacity: checked ? 0.65 : bloqueado ? 0.5 : 1,
                        }}
                      >
                        <Thumb
                          src={getImagenUrl(pr.imagen_url)}
                          size={esCocinero ? 40 : 52}
                          radius={esCocinero ? 10 : 12}
                        />
                        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: bloqueado ? COL.faint : checked ? COL.muted : COL.ink,
                              textDecoration: checked ? 'line-through' : 'none',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '100%',
                            }}
                          >
                            {pr.descripcion || pr.nombre || `Producto ${i + 1}`}
                            {bloqueado && (
                              <span
                                style={{
                                  fontSize: '10px',
                                  color: COL.orange,
                                  fontWeight: 700,
                                  marginLeft: 6,
                                }}
                              >
                                (Cocina)
                              </span>
                            )}
                          </div>
                          {pr.combo_nombre && (
                            <div
                              style={{
                                fontSize: '10px',
                                color: COL.faint,
                                marginTop: '1px',
                              }}
                            >
                              Parte de: {pr.combo_nombre}
                            </div>
                          )}
                          <div
                            style={{
                              fontSize: '11px',
                              color: COL.muted,
                              marginTop: '1px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {pr.referencia ? `Ref: ${pr.referencia}` : ''}
                            {pr.codigo && !pr.referencia ? `Cod: ${pr.codigo}` : ''}
                            {(pr.referencia || pr.codigo) && (
                              <span style={{ margin: '0 4px', color: COL.faint }}>|</span>
                            )}
                            {pr.stock != null
                              ? `Stock: ${Number(pr.stock).toFixed(pr.pesable ? 3 : 0)}`
                              : ''}
                            {pr.marca ? <span> | {pr.marca}</span> : ''}
                            {pr.modelo ? <span> | {pr.modelo}</span> : ''}
                          </div>
                          {(pr.etiquetas || []).length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '3px',
                                marginTop: '3px',
                              }}
                            >
                              {pr.etiquetas.map((et, ei) => (
                                <span
                                  key={ei}
                                  style={{
                                    fontSize: '9px',
                                    fontWeight: 700,
                                    background: isDark() ? '#78350f' : '#fff4ec',
                                    color: isDark() ? '#fbbf24' : '#b35100',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    border: '1px solid ' + (isDark() ? '#fbbf24' : '#ffd6a8'),
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {et.nombre}
                                  {et.precio > 0 ? ` +$${fmtMonto(et.precio)}` : ''}
                                </span>
                              ))}
                            </div>
                          )}
                          {pr.combo_id && Array.isArray(pr.componentes) && pr.componentes.length > 0 && (
                            <div style={{ marginTop: '4px' }}>
                              <div
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  color: COL.muted,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.4px',
                                  marginBottom: '3px',
                                }}
                              >
                                {esCocinero ? 'Componentes de cocina' : 'Componentes del combo'}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {(esCocinero ? pr.componentes_cocina || pr.componentes : pr.componentes).map((comp, ci) => {
                                  const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
                                  return (
                                    <div
                                      key={ci}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontSize: '11px',
                                        color: COL.muted,
                                        paddingLeft: '2px',
                                      }}
                                    >
                                      <span
                                        style={{
                                          display: 'inline-block',
                                          width: 4,
                                          height: 4,
                                          borderRadius: '50%',
                                          backgroundColor: '#f0b400',
                                          flexShrink: 0,
                                        }}
                                      />
                                      <span style={{ fontWeight: 500, color: COL.ink }}>{comp.nombre}</span>
                                      {compEtiq.length > 0 && (
                                        <span style={{ color: COL.faint }}>
                                          ({compEtiq.map((e) => e.nombre).join(', ')})
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div style={{ fontSize: '12px', color: COL.faint }}>
                            {cantidadLabel(pr)}
                            {pr.tiempo_preparacion_min > 0
                              ? ` · ~${pr.tiempo_preparacion_min}min prep`
                              : ''}
                            {pr.es_cocina ? ' · Cocina' : ''}
                          </div>
                        </div>
                        {infoBtn(pr)}
                        <div
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '8px',
                            border: `2px solid ${bloqueado ? COL.faint : checked ? COL.green : COL.faint}`,
                            background: bloqueado ? COL.line : checked ? COL.green : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {bloqueado ? (
                            <FiLock size={12} color={COL.faint} />
                          ) : (
                            checked && <FiCheck size={14} color="#fff" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {interactivo && !listo && (
                <button
                  onClick={() => marcarListo(detalle.id)}
                  disabled={!todoMarcado}
                  style={{
                    marginTop: '14px',
                    width: '100%',
                    padding: '14px',
                    borderRadius: '14px',
                    border: 'none',
                    fontSize: '15px',
                    fontWeight: 800,
                    cursor: todoMarcado ? 'pointer' : 'not-allowed',
                    color: '#fff',
                    background: todoMarcado ? COL.green : COL.faint,
                    letterSpacing: '0.5px',
                  }}
                >
                  {todoMarcado
                    ? esCocinero
                      ? 'Marcar cocina como listo'
                      : 'Marcar armado como listo'
                    : `Faltan ${visibles.length - hechos} items`}
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              padding: '8px 16px',
              borderTop: `1px solid ${COL.border}`,
              background: COL.card,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '12px',
                background: COL.line,
              }}
            >
              <FiSearch size={14} color={COL.faint} />
              <input
                value={scanValue}
                onChange={(e) => {
                  setScanValue(e.target.value);
                  onScan(e.target.value);
                }}
                placeholder="Escanear codigo de barra..."
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontSize: '13px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  color: COL.text,
                }}
              />
              {scanMsg === 'ok' && <FiCheck size={14} color={COL.green} />}
              {scanMsg === 'err' && <FiX size={14} color={COL.red} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const onlineItems =
    tab === 'por_armar'
      ? porArmar.filter((p) => {
          const prods = parseProductos(p.productos);
          if (esCocinero) {
            // 'armado' es el resultado del propio trabajo de cocina en tienda:
            // el cocinero debe seguir viendolo (ya completado). En online, 'armado'
            // es trabajo del armador y no le corresponde a cocina.
            if (p.estado === 'listo') return false;
            if (p.estado === 'armado' && p.tipo_pedido !== 'tienda') return false;
            return prods.some((pr) => pr.es_cocina === true);
          }
          return prods.length > 0;
        })
      : listosHoy.filter((p) => {
          const isTienda = p.tipo_pedido === 'tienda' || !!p.mesa_label || !!p.mesa_id;
          if (isTienda) return false;
          return !esCocinero || parseProductos(p.productos).some((pr) => pr.es_cocina);
        });
  const tiendaItems =
    tab === 'por_armar'
      ? comandas.filter((c) => {
          const prods = parseProductos(c.productos);
          if (esCocinero) return prods.some((pr) => pr.es_cocina === true);
          return prods.length > 0;
        })
      : listosHoy.filter((p) => p.tipo_pedido === 'tienda' || !!p.mesa_label || !!p.mesa_id);
  const lista = (
    filtroTipo === 'todas'
      ? [...onlineItems, ...tiendaItems]
      : filtroTipo === 'online'
        ? onlineItems
        : tiendaItems
  ).sort((a, b) => {
    // Prioridad siempre al frente (override visual del chip "Prioridad").
    const pa = a.prioridad ? 1 : 0;
    const pb = b.prioridad ? 1 : 0;
    if (pa !== pb) return pb - pa;
    if (tab !== 'por_armar') {
      // Listos: recien terminados primero.
      return new Date(b.hora_armado || b.fechapedido || 0) - new Date(a.hora_armado || a.fechapedido || 0);
    }
    // Por armar: orden de llegada (FIFO), el mas antiguo primero.
    return new Date(a.fechapedido || 0) - new Date(b.fechapedido || 0);
  });
  const listaFiltrada = textoBusqueda.trim()
    ? lista.filter((p) => {
        const q = textoBusqueda.toLowerCase();
        return (
          (p.mesa_nombre || '').toLowerCase().includes(q) ||
          String(p.mesa_codigo || '')
            .toLowerCase()
            .includes(q) ||
          String(p.mesa_id || '').includes(q) ||
          (p.cliente || '').toLowerCase().includes(q) ||
          String(p.id).includes(q)
        );
      })
    : lista;

  // Color estable por mesa (paleta). Compartido por las vistas plana y agrupada.
  const mesaColor = (key) => {
    const s = String(key || '');
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return MESA_PALETTE[h % MESA_PALETTE.length];
  };

  // Etiqueta de entrega para pedidos online (delivery / retiro / encomienda).
  const etiquetaEntrega = (p) => {
    const t = (p.tipo_entrega || '').toLowerCase();
    if (t === 'retiro') return 'Retiro';
    if (t === 'encomienda') return 'Encomienda';
    return 'Delivery';
  };

  // Lista PLANA en orden de llegada (FIFO), intercalando tienda y online tal cual
  // llegaron. Para cada pedido de tienda se emite un encabezado "Mesa N" (re-emitido
  // cuando una mesa reaparece tras una interrupcion: otra mesa o un pedido online).
  // Los online se identifican individualmente como Delivery / Retiro / Encomienda.
  const filas = [];
  {
    let lastMesaKey = null;
    let headerSeq = 0;
    listaFiltrada.forEach((p) => {
      const isTienda = p.tipo_pedido === 'tienda' || !!p.mesa_label || !!p.mesa_id;
      if (isTienda) {
        const key = p.mesa_id != null ? `m${p.mesa_id}` : `l${p.mesa_label || 'sinmesa'}`;
        const label = p.mesa_label
          ? `Mesa ${p.mesa_label}`
          : p.mesa_id != null
            ? `Mesa #${p.mesa_id}`
            : 'Sin mesa';
        if (key !== lastMesaKey) {
          // uid unico por encabezado: una misma mesa puede reaparecer varias veces
          // (FIFO intercalado) y su `key` (`m{id}`) se repite. Sin un uid, React
          // recibiria claves duplicadas y reconciliaria mal el DOM (encabezados que
          // se acumulan/desalinean).
          filas.push({ type: 'header', key, uid: `h-${headerSeq++}`, label, color: mesaColor(key), esOnline: false });
          lastMesaKey = key;
        }
        filas.push({ type: 'item', p, esOnline: false, colorBanner: mesaColor(key) });
      } else {
        lastMesaKey = null;
        filas.push({ type: 'item', p, esOnline: true, colorBanner: COL.blue });
      }
    });
  }

  const tipoBtns = [
    { id: 'todas', label: 'Todas' },
    { id: 'online', label: 'Online' },
    { id: 'tienda', label: 'Tienda' },
  ];
  const tipoBtnStyle = (id) => ({
    flex: 1,
    border: 'none',
    borderRadius: '800px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    background: filtroTipo === id ? COL.ink : COL.line,
    color: filtroTipo === id ? '#fff' : COL.muted,
  });

  // ════════════ VISTA PLANA (solo restaurante) ════════════
  if (esRestaurante) {
    const puedeInteractuar = (p) =>
      !isReadOnly &&
      !esListo(p) &&
      !(esCocinero && p.estado === 'armado') &&
      (esCocinero ? cocineroGestionar : true);

    // Chip de personalizacion: muy visible, para que cocina/armado lo lean de un vistazo.
    const chipEtiqStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
      fontWeight: 700,
      lineHeight: 1,
      padding: '4px 9px',
      borderRadius: '999px',
      background: isDark() ? '#9f1239' : '#fff1f2',
      border: `1px solid ${isDark() ? '#fecdd3' : '#fda4af'}`,
      color: isDark() ? '#fecdd3' : '#be123c',
      whiteSpace: 'nowrap',
    };
    const chipEtiqPrecioStyle = { fontSize: '10px', fontWeight: 600, opacity: 0.85 };

    const renderEtiquetas = (ets, prefix = '') => {
      if (!Array.isArray(ets) || ets.length === 0) return null;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
          {ets.map((et, ei) => (
            <span key={ei} style={chipEtiqStyle}>
              <FiTag size={11} />
              {prefix}
              {et.nombre || et.id}
              {Number(et.precio) > 0 && (
                <span style={chipEtiqPrecioStyle}>
                  +${fmtMonto(et.precio)}
                </span>
              )}
            </span>
          ))}
        </div>
      );
    };

    // Componentes de combo con sus personalizaciones (clave para el armador).
    const renderComboComponentes = (pr) => {
      const comps = pr.componentes;
      if (!pr.combo_id || !Array.isArray(comps) || comps.length === 0) return null;
      return (
        <div style={{ marginTop: '6px' }}>
          <div
            style={{
              fontSize: '9px',
              fontWeight: 800,
              color: COL.muted,
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '3px',
            }}
          >
            Componentes del combo
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {comps.map((comp, ci) => {
              const compEtiq = Array.isArray(comp.etiquetas) ? comp.etiquetas : [];
              return (
                <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      fontSize: '11px',
                      color: COL.ink,
                      paddingLeft: '2px',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: '#f0b400',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontWeight: 600 }}>{comp.nombre || comp.codigo_producto}</span>
                  </div>
                  {compEtiq.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', paddingLeft: '9px' }}>
                      {compEtiq.map((e, ei) => (
                        <span key={ei} style={chipEtiqStyle}>
                          <FiTag size={10} />
                          {e.nombre || e.id}
                          {Number(e.precio) > 0 && (
                            <span style={chipEtiqPrecioStyle}>+${fmtMonto(e.precio)}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div style={{ height: '100%', width: '100%', background: COL.bg, overflow: 'hidden' }}>
        {styleTag}
        {confirmModal}
        {recetaModal}
        <div className="arm-wrap">
          <div
            className="arm-bar"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              background: COL.card,
              borderBottom: `1px solid ${COL.border}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '10px',
                  background: (esCocinero ? COL.red : COL.orange) + '18',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {esCocinero ? (
                  <FiCoffee size={18} color={COL.red} />
                ) : (
                  <FiPackage size={18} color={COL.orange} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '17px', fontWeight: 800, color: COL.ink, lineHeight: 1 }}>
                  {esCocinero ? 'Cocina' : 'Armado'}
                </div>
                <div style={{ fontSize: '11px', color: COL.faint, marginTop: '2px' }}>
                  {listaFiltrada.length} por armar · {listosHoy.length} listos hoy
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                background: COL.line,
                borderRadius: '12px',
                padding: '3px',
                gap: '3px',
              }}
            >
              <button
                onClick={() => setTab('por_armar')}
                style={{
                  flex: 1,
                  border: 'none',
                  background: tab === 'por_armar' ? COL.card : 'transparent',
                  borderRadius: '10px',
                  padding: '8px 6px',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: tab === 'por_armar' ? COL.ink : COL.muted,
                  cursor: 'pointer',
                  boxShadow: tab === 'por_armar' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
                }}
              >
                Por armar
              </button>
              <button
                onClick={() => setTab('listos')}
                style={{
                  flex: 1,
                  border: 'none',
                  background: tab === 'listos' ? COL.card : 'transparent',
                  borderRadius: '10px',
                  padding: '8px 6px',
                  fontSize: '13px',
                  fontWeight: 700,
                  color: tab === 'listos' ? COL.ink : COL.muted,
                  cursor: 'pointer',
                  boxShadow: tab === 'listos' ? '0 1px 3px rgba(11,26,74,0.12)' : 'none',
                }}
              >
                Listos
              </button>
            </div>
          </div>

          <div className="arm-scroll" style={{ padding: '12px 14px' }}>
            {listaFiltrada.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: COL.faint }}>
                <FiPackage size={42} />
                <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 600 }}>
                  {tab === 'por_armar' ? 'No hay pedidos por armar' : 'Aun no hay pedidos listos hoy'}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filas.map((f) => {
                  if (f.type === 'header') {
                    return (
                      <div
                        key={f.uid}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 16px',
                          borderRadius: '14px',
                          background: f.color,
                          color: '#fff',
                          boxShadow: `0 4px 14px ${f.color}55`,
                        }}
                      >
                        <FiHome size={18} />
                        <span style={{ fontSize: '17px', fontWeight: 800, letterSpacing: '0.3px' }}>
                          {f.label}
                        </span>
                      </div>
                    );
                  }
                  const p = f.p;
                  const esOnline = f.esOnline;
                  const colorBanner = f.colorBanner;
                  const prods = productosVisibles(p);
                  const listo = esListo(p);
                  return (
                    <div
                      key={p.id}
                            style={{
                              background: COL.card,
                              borderRadius: '16px',
                              border: `1px solid ${COL.border}`,
                              borderLeft: `4px solid ${listo ? COL.green : colorBanner}`,
                              boxShadow: '0 1px 3px rgba(11,26,74,0.04)',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 14px',
                                borderBottom: `1px solid ${COL.line}`,
                              }}
                            >
                              <span style={{ fontSize: '15px', fontWeight: 800, color: COL.ink }}>
                                Pedido #{p.ticket_numero || p.id}
                              </span>
                              {esOnline && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    color: '#fff',
                                    background: p.tipo_entrega === 'retiro' ? '#0891b2' : '#2563eb',
                                    padding: '3px 9px',
                                    borderRadius: '999px',
                                  }}
                                >
                                  {p.tipo_entrega === 'retiro' ? <FiHome size={11} /> : <FiTruck size={11} />}
                                  {etiquetaEntrega(p)}
                                </span>
                              )}
                              {p.prioridad && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    color: '#b45309',
                                    background: '#fef3c7',
                                    padding: '3px 9px',
                                    borderRadius: '999px',
                                  }}
                                >
                                  <FiStar size={11} color="#f59e0b" fill="#f59e0b" /> Prioridad
                                </span>
                              )}
                              {p.cliente && (
                                <span
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px',
                                    fontSize: '13px',
                                    fontWeight: 800,
                                    color: COL.ink,
                                    background: COL.priLight || COL.blueBg,
                                    padding: '3px 10px',
                                    borderRadius: '9px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  <FiUser size={13} />
                                  {p.cliente}
                                </span>
                              )}
                              {listo && (
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    color: COL.green,
                                    background: COL.green + '14',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                  }}
                                >
                                  <FiCheckCircle size={11} /> Listo
                                </span>
                              )}
                              {!listo && p.pagado && (
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    color: COL.blue,
                                    background: COL.blue + '14',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                  }}
                                >
                                  <FiCheckCircle size={11} /> Pagado
                                </span>
                              )}
                              {!listo && esCocinero && p.estado === 'armado' && (
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    color: COL.orange,
                                    background: COL.orange + '14',
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                  }}
                                >
                                  <FiCheckCircle size={11} /> Cocina completada
                                </span>
                              )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              {puedeInteractuar(p) && !listo && prods.length > 0 && (
                                <div style={{ padding: '8px 14px 0' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      pedirConfirmacion(
                                        '¿Tachar todos los artículos de este pedido?',
                                        () => marcarTodosRestaurante(p)
                                      );
                                    }}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '5px',
                                      border: `1px solid ${COL.green}`,
                                      background: COL.green + '14',
                                      color: COL.green,
                                      borderRadius: '999px',
                                      padding: '4px 11px',
                                      fontSize: '11px',
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      fontFamily: 'inherit',
                                    }}
                                  >
                                    <FiCheck size={12} /> Tachar todos
                                  </button>
                                </div>
                              )}
                              {prods.length === 0 && (
                                <div style={{ padding: '12px 14px', fontSize: '12px', color: COL.faint }}>
                                  Sin items para mostrar
                                </div>
                              )}
                              {prods.map((pr, i) => {
                                const checked = isChecked(pr);
                                const bloqueado = esItemBloqueado(pr);
                                const puede = puedeInteractuar(p) && !bloqueado;
                                return (
                                  <div
                                    key={i}
                                    onClick={() => puede && toggleItemRestaurante(p, pr)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      padding: '10px 14px',
                                      borderTop: i > 0 ? `1px solid ${COL.line}` : 'none',
                                      background: checked
                                        ? isDark()
                                          ? '#064e3b'
                                          : '#ecfdf5'
                                        : bloqueado
                                          ? isDark()
                                            ? '#1e293b'
                                            : '#fafafa'
                                          : 'transparent',
                                      cursor: puede ? 'pointer' : 'default',
                                      opacity: checked ? 0.65 : bloqueado ? 0.5 : 1,
                                    }}
                                  >
                                    <Thumb src={getImagenUrl(pr.imagen_url)} size={40} radius={9} />
                                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                      <div
                                        style={{
                                          fontSize: '14px',
                                          fontWeight: 600,
                                          color: bloqueado ? COL.faint : checked ? COL.muted : COL.ink,
                                          textDecoration: checked ? 'line-through' : 'none',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {pr.descripcion || pr.nombre || `Producto ${i + 1}`}
                                        {bloqueado && (
                                          <span
                                            style={{
                                              fontSize: '10px',
                                              color: COL.orange,
                                              fontWeight: 700,
                                              marginLeft: 6,
                                            }}
                                          >
                                            (Cocina)
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ fontSize: '11px', color: COL.muted, marginTop: '1px' }}>
                                        {cantidadLabel(pr)}
                                        {pr.tiempo_preparacion_min > 0
                                          ? ` · ~${pr.tiempo_preparacion_min}min`
                                          : ''}
                                        {pr.es_cocina && !esCocinero ? ' · Cocina' : ''}
                                        {pr.combo_nombre ? ` · ${pr.combo_nombre}` : ''}
                                      </div>
                                      {renderEtiquetas(pr.etiquetas)}
                                      {renderComboComponentes(pr)}
                                    </div>
                                    {infoBtn(pr)}
                                    <div
                                      style={{
                                        width: '26px',
                                        height: '26px',
                                        borderRadius: '8px',
                                        border: `2px solid ${bloqueado ? COL.faint : checked ? COL.green : COL.faint}`,
                                        background: bloqueado ? COL.line : checked ? COL.green : 'transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {bloqueado ? (
                                        <FiLock size={12} color={COL.faint} />
                                      ) : (
                                        checked && <FiCheck size={14} color="#fff" />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

    return (
      <div style={{ height: '100%', width: '100%', background: COL.bg, overflow: 'hidden' }}>
        {styleTag}
        {confirmModal}
        {recetaModal}
        <div className="arm-wrap">
          <div
            className="arm-bar"
            style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '14px 16px',
            background: COL.card,
            borderBottom: `1px solid ${COL.border}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                background: (esCocinero ? COL.red : COL.orange) + '18',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {esCocinero ? (
                <FiCoffee size={18} color={COL.red} />
              ) : (
                <FiPackage size={18} color={COL.orange} />
              )}
            </div>
            <div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: COL.ink, lineHeight: 1 }}>
                {esCocinero ? 'Cocina' : 'Armado'}
                <span
                  title={socketConectado ? 'Conectado' : 'Desconectado'}
                  style={{
                    display: 'inline-block',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: socketConectado ? COL.green : COL.red,
                    marginLeft: 8,
                    verticalAlign: 'middle',
                    boxShadow: `0 0 0 2px ${socketConectado ? COL.green : COL.red}33`,
                  }}
                />
              </div>
              <div style={{ fontSize: '11px', color: COL.faint, marginTop: '2px' }}>
                {onlineItems.length + tiendaItems.length} por armar · {listosHoy.length} listos hoy
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {tipoBtns.map((t) => (
              <button key={t.id} onClick={() => setFiltroTipo(t.id)} style={tipoBtnStyle(t.id)}>
                {t.id === 'tienda' && (
                  <FiHome size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                )}
                {t.id === 'online' && (
                  <FiBox size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                )}
                {t.label}
              </button>
            ))}
          </div>
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 10px',
                borderRadius: '10px',
                background: COL.line,
              }}
            >
              <FiSearch size={14} color={COL.faint} />
              <input
                value={textoBusqueda}
                onChange={(e) => setTextoBusqueda(e.target.value)}
                placeholder="Buscar mesa, cliente, #id..."
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  fontSize: '12px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  color: COL.text,
                }}
              />
              {textoBusqueda && (
                <button
                  onClick={() => setTextoBusqueda('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: COL.muted,
                    padding: 0,
                    display: 'flex',
                  }}
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              background: COL.line,
              borderRadius: '12px',
              padding: '3px',
              gap: '3px',
            }}
          >
            <button
              onClick={() => setTab('por_armar')}
              style={{
                flex: 1,
                border: 'none',
                background: tab === 'por_armar' ? COL.card : 'transparent',
                borderRadius: '10px',
                padding: '8px 6px',
                fontSize: '13px',
                fontWeight: 700,
                color: tab === 'por_armar' ? COL.ink : COL.muted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: tab === 'por_armar' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none',
              }}
            >
              Por armar
            </button>
            <button
              onClick={() => setTab('listos')}
              style={{
                flex: 1,
                border: 'none',
                background: tab === 'listos' ? COL.card : 'transparent',
                borderRadius: '10px',
                padding: '8px 6px',
                fontSize: '13px',
                fontWeight: 700,
                color: tab === 'listos' ? COL.ink : COL.muted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: tab === 'listos' ? '0 1px 3px rgba(11,26,74,0.12)' : 'none',
              }}
            >
              Listos
            </button>
          </div>
        </div>

        <div className="arm-scroll" style={{ padding: '12px 14px' }}>
          {isReadOnly && !esCocinero && (
            <div
              style={{
                padding: '10px 14px',
                marginBottom: '12px',
                borderRadius: '10px',
                background: 'var(--agx-amber-bg)',
                border: '1px solid var(--agx-amber)',
                color: 'var(--agx-warning-text)',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FiLock size={14} /> Solo tienes acceso de lectura. No puedes gestionar armado.
            </div>
          )}
          {listaFiltrada.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: COL.faint }}>
              {tab === 'por_armar' ? <FiPackage size={42} /> : <FiBox size={42} />}
              <div style={{ marginTop: '12px', fontSize: '14px', fontWeight: 600 }}>
                {tab === 'por_armar' ? 'No hay pedidos por armar' : 'Aun no hay pedidos listos hoy'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filas.map((f) => {
                if (f.type === 'header') {
                  return (
                    <div
                      key={f.uid}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 4px 2px',
                        fontSize: '12px',
                        fontWeight: 800,
                        letterSpacing: '0.4px',
                        textTransform: 'uppercase',
                        color: COL.tienda,
                      }}
                    >
                      <FiHome size={13} />
                      {f.label}
                    </div>
                  );
                }
                const p = f.p;
                const prods = productosVisibles(p);
                const allProds = parseProductos(p.productos);
                const cocoStats = cocinaCompletados(p);
                const etiquetasUnicas = (() => {
                  const names = [];
                  allProds.forEach((pr) => {
                    (pr.etiquetas || []).forEach((e) => names.push(e.nombre));
                  });
                  return [...new Set(names)].slice(0, 4);
                })();
                const thumbs = prods.slice(0, 3);
                const extra = prods.length - thumbs.length;
                const mio = esMio(p);
                const locked = bloqueado(p);
                const listo = esListo(p);
                const isTienda = p.tipo_pedido === 'tienda' || !!p.mesa_label || !!p.mesa_id;
                const onlineColor = COL.blue;
                const tiendaColor = COL.tienda;
                const leftColor = isTienda
                  ? listo
                    ? COL.green
                    : tiendaColor
                  : listo
                    ? COL.green
                    : mio
                      ? COL.orange
                      : locked
                        ? COL.faint
                        : onlineColor;
                const cardBorderColor = isTienda ? tiendaColor + '33' : COL.border;
                const tipoTagBg = isTienda ? tiendaColor + '18' : onlineColor + '18';
                const tipoTagColor = isTienda ? tiendaColor : onlineColor;
                return (
                  <div
                    key={p.id}
                    className="arm-row"
                    onClick={() => setDetalleId(p.id)}
                    style={{
                      background: COL.card,
                      borderRadius: '16px',
                      padding: '12px',
                      cursor: 'pointer',
                      border: `1px solid ${mio ? COL.orange + '66' : cardBorderColor}`,
                      borderLeft: `4px solid ${leftColor}`,
                      opacity: locked ? 0.7 : 1,
                      boxShadow: '0 1px 3px rgba(11,26,74,0.04)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ display: 'flex' }}>
                        {thumbs.map((tp, i) => (
                          <div
                            key={i}
                            style={{
                              marginLeft: i === 0 ? 0 : -10,
                              border: `2px solid ${COL.card}`,
                              borderRadius: '11px',
                              zIndex: 3 - i,
                            }}
                          >
                            <Thumb src={getImagenUrl(tp.imagen_url)} size={40} radius={9} />
                          </div>
                        ))}
                        {extra > 0 && (
                          <div
                            style={{
                              marginLeft: -10,
                              width: 40,
                              height: 40,
                              borderRadius: '11px',
                              border: `2px solid ${COL.card}`,
                              background: COL.line,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: 700,
                              color: COL.muted,
                              zIndex: 0,
                            }}
                          >
                            +{extra}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span style={{ fontSize: '15px', fontWeight: 800, color: COL.ink }}>
                            #{p.ticket_numero || p.id}
                          </span>
                          {p.prioridad && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '11px',
                                fontWeight: 800,
                                color: '#b45309',
                                background: '#fef3c7',
                                padding: '3px 9px',
                                borderRadius: '999px',
                              }}
                            >
                              <FiStar size={11} color="#f59e0b" fill="#f59e0b" /> Prioridad
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: '11px',
                              padding: '3px 8px',
                              borderRadius: '8px',
                              fontWeight: 700,
                              background: tipoTagBg,
                              color: tipoTagColor,
                            }}
                          >
                            {isTienda ? (
                              <>
                                <FiHome
                                  size={10}
                                  style={{ marginRight: 3, verticalAlign: 'middle' }}
                                />
                                Tienda
                              </>
                            ) : (
                              <>
                                {p.tipo_entrega === 'retiro' ? (
                                  <FiHome
                                    size={10}
                                    style={{ marginRight: 3, verticalAlign: 'middle' }}
                                  />
                                ) : (
                                  <FiTruck
                                    size={10}
                                    style={{ marginRight: 3, verticalAlign: 'middle' }}
                                  />
                                )}
                                {etiquetaEntrega(p)}
                              </>
                            )}
                          </span>
                          {isTienda && p.mesa_label && (
                            <span
                              style={{
                                fontSize: '11px',
                                color: tiendaColor,
                                background: tiendaColor + '14',
                                padding: '2px 8px',
                                borderRadius: '8px',
                                fontWeight: 700,
                              }}
                            >
                              {p.mesa_label}
                            </span>
                          )}
                          {p.cliente && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                fontSize: '13px',
                                fontWeight: 800,
                                color: COL.ink,
                                background: COL.priLight || COL.blueBg,
                                padding: '3px 10px',
                                borderRadius: '9px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              <FiUser size={13} />
                              {p.cliente}
                            </span>
                          )}
                        </div>
                        {esCocinero && tieneItemsCocina(p) && (
                          <div style={{ fontSize: '11px', color: COL.red, marginTop: '2px' }}>
                            <FiCoffee size={10} style={{ marginRight: 4 }} />
                            {prods.length} items cocina
                          </div>
                        )}
                        {!esCocinero && (
                          <div style={{ fontSize: '12px', color: COL.muted, marginTop: '2px' }}>
                            {prods.length} producto(s)
                            {cocoStats && cocoStats.total > 0 && (
                              <span
                                style={{
                                  color:
                                    cocoStats.completados === cocoStats.total
                                      ? COL.green
                                      : COL.orange,
                                  fontWeight: 600,
                                  marginLeft: '6px',
                                }}
                              >
                                <FiCoffee
                                  size={10}
                                  style={{ marginRight: 3, verticalAlign: 'middle' }}
                                />
                                {cocoStats.completados}/{cocoStats.total}
                              </span>
                            )}
                            {v('vis.pedidos.total') && (
                              <>
                                {' '}
                                ·{' '}
                                <strong style={{ color: COL.ink }}>
                                  ${typeof p.total === 'number' ? fmtMonto(p.total) : p.total}
                                </strong>
                              </>
                            )}
                          </div>
                        )}
                        {etiquetasUnicas.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '3px',
                              marginTop: '3px',
                            }}
                          >
                            {etiquetasUnicas.map((name, ei) => (
                              <span
                                key={ei}
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  background: isDark() ? '#78350f' : '#fff4ec',
                                  color: isDark() ? '#fbbf24' : '#b35100',
                                  padding: '2px 5px',
                                  borderRadius: '3px',
                                  border: '1px solid ' + (isDark() ? '#fbbf24' : '#ffd6a8'),
                                }}
                              >
                                {name}
                              </span>
                            ))}
                            {(() => {
                              const allNombres = [];
                              allProds.forEach((pr) => {
                                (pr.etiquetas || []).forEach((e) => allNombres.push(e.nombre));
                              });
                              return allNombres.length > 4 ? (
                                <span
                                  style={{
                                    fontSize: '9px',
                                    fontWeight: 600,
                                    color: COL.faint,
                                    padding: '2px 4px',
                                  }}
                                >
                                  +{allNombres.length - 4}
                                </span>
                              ) : null;
                            })()}
                          </div>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '4px',
                          }}
                        >
                          {p.sede_nombre && (
                            <span
                              style={{
                                fontSize: '11px',
                                color: COL.faint,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                              }}
                            >
                              <FiHome size={11} /> {p.sede_nombre}
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: '11px',
                              color: COL.faint,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px',
                            }}
                          >
                            <FiClock size={11} />{' '}
                            {formatearHora(
                              listo ? p.hora_armado || p.fecha_actualizacion : p.fechapedido
                            )}
                          </span>
                          {!listo && minutosDesde(p.fechapedido) > SLA_MIN && (
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: COL.red,
                                background: COL.red + '14',
                                padding: '1px 7px',
                                borderRadius: '999px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                              }}
                            >
                              <FiAlertTriangle size={10} /> {minutosDesde(p.fechapedido)} min
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '4px',
                          flexShrink: 0,
                        }}
                      >
                        {listo && (
                          <span
                            style={{ ...S.tag, color: COL.green, background: COL.green + '14' }}
                          >
                            <FiCheckCircle size={11} /> Listo
                          </span>
                        )}
                        {!listo && p.pagado && (
                          <span
                            style={{ ...S.tag, color: COL.blue, background: COL.blue + '14' }}
                          >
                            <FiCheckCircle size={11} /> Pagado
                          </span>
                        )}
                        {!listo && mio && (
                          <span
                            style={{ ...S.tag, color: COL.orange, background: COL.orange + '14' }}
                          >
                            Tuyo
                          </span>
                        )}
                        {!listo && locked && (
                          <span style={{ ...S.tag, color: COL.muted, background: COL.line }}>
                            <FiUser size={10} /> {p.armador_nombre || p.id_cocinero || 'Ocupado'}
                          </span>
                        )}
                        {!listo && esLibre(p) && (
                          <span
                            style={{ ...S.tag, color: COL.orange, background: COL.orange + '14' }}
                          >
                            Por armar
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const S = {
  chip: {
    fontSize: '12px',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '999px',
  },
  tag: {
    fontSize: '11px',
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
  },
};

export default ArmadorPanel;
